import { useState, useEffect, useCallback, useRef } from 'react';

const OFFLINE_QUEUE_KEY = 'thrive-offline-queue';

export function useOfflineSupport() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const swRegistration = useRef(null);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((reg) => {
          swRegistration.current = reg;
        })
        .catch(() => { /* SW registration failed */ });
      
      // Listen for messages from SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'QUEUE_REQUEST') {
          setOfflineQueue(prev => {
            const updated = [...prev, event.data.data];
            localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(updated));
            return updated;
          });
        }
        if (event.data.type === 'SYNC_COMPLETE') {
          setSyncing(false);
          const successful = event.data.results.filter(r => r.success).length;
          const total = event.data.results.length;
          if (total > 0) {
          }
          setOfflineQueue([]);
          localStorage.removeItem(OFFLINE_QUEUE_KEY);
        }
      });
    }

    // Load persisted queue
    const saved = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (saved) {
      try { setOfflineQueue(JSON.parse(saved)); } catch {}
    }
  }, []);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncQueue();
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync queued requests when back online
  const syncQueue = useCallback(() => {
    const saved = localStorage.getItem(OFFLINE_QUEUE_KEY);
    const queue = saved ? JSON.parse(saved) : [];
    
    if (queue.length === 0 || !navigator.serviceWorker.controller) return;
    
    setSyncing(true);
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_QUEUE',
      queue
    });
  }, []);

  return {
    isOnline,
    offlineQueue,
    syncing,
    queueLength: offlineQueue.length,
    syncQueue
  };
}
